/**
 * Error component for VulForm
 */

import React from 'react';
import { FormError as IFormError } from '@vulform/core';

interface FormErrorProps {
  error: IFormError | { code: string; message: string };
  onRetry?: () => void;
  className?: string;
  style?: React.CSSProperties;
}

export const FormError: React.FC<FormErrorProps> = ({ error, onRetry, className = '', style }) => {
  const getErrorMessage = () => {
    switch (error.code) {
      case 'MISSING_API_KEY':
        return 'Configuration error: API key is missing.';
      case 'INVALID_API_KEY':
        return 'Configuration error: Invalid API key format.';
      case 'TEMPLATE_NOT_FOUND':
        return 'Form template not found. Please check the template ID.';
      case 'NETWORK_ERROR':
        return 'Network error. Please check your internet connection and try again.';
      case 'RATE_LIMIT_EXCEEDED':
        return 'Too many requests. Please wait a moment before trying again.';
      case 'TIMEOUT':
        return 'Request timeout. Please try again.';
      default:
        return error.message || 'An unexpected error occurred.';
    }
  };

  const getErrorTitle = () => {
    switch (error.code) {
      case 'MISSING_API_KEY':
      case 'INVALID_API_KEY':
        return 'Configuration Error';
      case 'TEMPLATE_NOT_FOUND':
        return 'Form Not Found';
      case 'NETWORK_ERROR':
      case 'TIMEOUT':
        return 'Connection Error';
      case 'RATE_LIMIT_EXCEEDED':
        return 'Rate Limit Exceeded';
      default:
        return 'Error';
    }
  };

  const showRetryButton = () => {
    return !['MISSING_API_KEY', 'INVALID_API_KEY', 'TEMPLATE_NOT_FOUND'].includes(error.code);
  };

  return (
    <div className={`vulform-error ${className}`} style={style}>
      <div className='text-center p-4 sm:p-6 border-2 border-red-200 bg-red-50 rounded-lg max-w-full'>
        {/* Error Icon */}
        <div className='mx-auto flex items-center justify-center h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-red-100 mb-3 sm:mb-4'>
          <svg
            className='h-5 w-5 sm:h-6 sm:w-6 text-red-600'
            fill='none'
            viewBox='0 0 24 24'
            stroke='currentColor'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth={2}
              d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z'
            />
          </svg>
        </div>

        {/* Error Title */}
        <h3 className='text-base sm:text-lg font-semibold text-red-900 mb-2'>{getErrorTitle()}</h3>

        {/* Error Message */}
        <p className='text-sm sm:text-base text-red-800 mb-4 sm:mb-6 leading-relaxed'>
          {getErrorMessage()}
        </p>

        {/* Error Details (Development only) */}
        {process.env.NODE_ENV === 'development' && (
          <details className='mb-4 sm:mb-6 text-left'>
            <summary className='cursor-pointer text-xs sm:text-sm text-red-700 hover:text-red-900 font-medium'>
              Technical Details
            </summary>
            <div className='mt-2 p-2 sm:p-3 bg-red-100 rounded text-xs text-red-800 font-mono overflow-x-auto'>
              <div className='whitespace-nowrap'>
                <strong>Code:</strong> {error.code}
              </div>
              <div className='whitespace-nowrap'>
                <strong>Message:</strong> {error.message}
              </div>
              {(error as IFormError).statusCode && (
                <div className='whitespace-nowrap'>
                  <strong>Status:</strong> {(error as IFormError).statusCode}
                </div>
              )}
            </div>
          </details>
        )}

        {/* Retry Button */}
        {showRetryButton() && onRetry && (
          <button
            onClick={onRetry}
            className='inline-flex items-center px-3 py-2 sm:px-4 sm:py-2 border border-transparent text-xs sm:text-sm font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors'
          >
            <svg
              className='h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2'
              fill='none'
              viewBox='0 0 24 24'
              stroke='currentColor'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15'
              />
            </svg>
            Try Again
          </button>
        )}
      </div>
    </div>
  );
};

export default FormError;
